import { Button } from '@/common/ui/button'
import { useMutateWithdrawTransaction } from '../../../api/hooks/useMutateWithdrawTransaction'
import { TransactionMethod, WithdrawStatus } from '@/schema'
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from '@/common/ui/alert-dialog'
import { Textarea } from '@/common/ui/textarea'
import { Label } from '@/common/ui/label'
import useQueryProfile from '@/modules/auth/api/hooks/useQueryProfile'
import { PermissionKeys } from '@/modules/manage-member/mappers'
import {
	CheckCircle,
	AlertTriangle,
	X,
	RotateCcw,
	RefreshCw,
} from 'lucide-react'
import { Icons } from '@/common/ui/icons'
import { useState } from 'react'
import UpdateWithdrawStatusModal from './UpdateWithdrawStatusModal'

interface WithdrawActionsProps {
	id: string
	status: WithdrawStatus
	processor: 'MANUAL' | 'AUTO'
	method?: TransactionMethod
	amount?: number
	playerBankProvider?: string
	playerBankAccountNumber?: string
	playerBankAccountName?: string
	timestamp?: { [key: string]: string }
	bankInfo?: string
}

export default function WithdrawActions({
	id,
	status,
	amount,
	processor,
	playerBankProvider,
	playerBankAccountNumber,
	playerBankAccountName,
	timestamp,
	bankInfo,
}: WithdrawActionsProps) {
	const { data: user } = useQueryProfile()
	const hasWritePermission = user?.userGroup.writePermission.includes(
		PermissionKeys.MANAGE_BANK_TRANSACTION,
	)

	const { approveWithdraw, cancel, refund, retryWithdraw } =
		useMutateWithdrawTransaction()
	const [cancelNote, setCancelNote] = useState('')
	const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false)
	const [approveNote, setApproveNote] = useState('')
	const [isApproveDialogOpen, setIsApproveDialogOpen] = useState(false)
	const [approveIsManual, setApproveIsManual] = useState<boolean | undefined>(
		undefined,
	)
	const [refundNote, setRefundNote] = useState('')
	const [isRefundDialogOpen, setIsRefundDialogOpen] = useState(false)
	const [isRetryDialogOpen, setIsRetryDialogOpen] = useState(false)

	const handleApproveWithdraw = (isManual: boolean, note?: string) => {
		approveWithdraw.mutate({ id, isManual, note })
		setIsApproveDialogOpen(false)
		setApproveNote('')
		setApproveIsManual(undefined)
	}

	const handleCancelWithdraw = () => {
		cancel.mutate({ id, note: cancelNote || undefined })
		setIsCancelDialogOpen(false)
		setCancelNote('')
	}

	const handleRefundWithdraw = () => {
		refund.mutate({ id, note: refundNote || undefined })
		setIsRefundDialogOpen(false)
		setRefundNote('')
	}

	const handleRetryWithdraw = () => {
		retryWithdraw.mutate(id)
		setIsRetryDialogOpen(false)
	}

	if (!hasWritePermission) return null

	// Check if transaction is TMN_WEBHOOK (should hide auto approval)
	const isTmnWebhook = bankInfo === 'TMN_WEBHOOK'

	return (
		<div className="flex items-center gap-2">
			<UpdateWithdrawStatusModal id={id} currentStatus={status} />
			{/* Approve Withdraw Actions - for PENDING/EXPIRED withdrawals */}
			{(status === 'PENDING' || status === 'EXPIRED') &&
				processor === 'MANUAL' && (
					<>
						{/* Auto Approval - sends to bank API (hidden for TMN_WEBHOOK) */}
						{!isTmnWebhook && (
							<AlertDialog
								open={isApproveDialogOpen && approveIsManual === false}
								onOpenChange={(open) => {
									setIsApproveDialogOpen(open)
									if (!open) {
										setApproveNote('')
										setApproveIsManual(undefined)
									}
								}}
							>
								<AlertDialogTrigger
									asChild
									onClick={() => {
										setIsApproveDialogOpen(true)
										setApproveIsManual(false)
									}}
								>
									<Button
										variant="default"
										size="sm"
										className="bg-blue-600 hover:bg-blue-700"
										disabled={approveWithdraw.isPending}
									>
										{approveWithdraw.isPending ? (
											<Icons.spinner className="mr-2 h-4 w-4 animate-spin" />
										) : (
											<CheckCircle className="mr-2 h-4 w-4" />
										)}
										อนุมัติแบบออโต้
									</Button>
								</AlertDialogTrigger>
								<AlertDialogContent className="max-w-md">
									<AlertDialogHeader>
										<div className="flex items-center gap-2">
											<AlertTriangle className="h-5 w-5 text-blue-500" />
											<AlertDialogTitle className="text-lg">
												ยืนยันการอนุมัติการถอนเงินแบบออโต้
											</AlertDialogTitle>
										</div>
										<AlertDialogDescription className="space-y-3 text-sm">
											<div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
												<div className="flex items-start gap-2">
													<AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-blue-500" />
													<div className="space-y-1">
														<p className="font-medium text-blue-800">
															การดำเนินการนี้จะส่งคำสั่งไปยังธนาคารผ่าน API
														</p>
														<p className="text-blue-700">
															เมื่อกดยืนยัน ระบบจะทำการโอนเงินจำนวน{' '}
															<span className="font-semibold">
																฿{amount?.toLocaleString()}
															</span>{' '}
															ไปยังบัญชี {playerBankProvider}{' '}
															{playerBankAccountNumber}{' '}
															ผ่านระบบธนาคารโดยอัตโนมัติ
														</p>
													</div>
												</div>
											</div>
											<div className="space-y-2">
												<div className="flex justify-between text-sm">
													<span>จำนวนเงิน:</span>
													<span className="font-semibold text-red-600">
														฿{amount?.toLocaleString()}
													</span>
												</div>
												<div className="flex justify-between text-sm">
													<span>บัญชีปลายทาง:</span>
													<span className="font-medium">
														{playerBankProvider} {playerBankAccountNumber}
													</span>
												</div>
												{playerBankAccountName && (
													<div className="flex justify-between text-sm">
														<span>ชื่อบัญชี:</span>
														<span className="font-medium">
															{playerBankAccountName}
														</span>
													</div>
												)}
											</div>
											<p className="text-xs text-gray-600">
												⚠️
												การดำเนินการนี้ไม่สามารถยกเลิกได้หลังจากที่ส่งคำสั่งไปยังธนาคารแล้ว
											</p>
										</AlertDialogDescription>
										<div className="space-y-2">
											<Label
												htmlFor="approve-auto-note"
												className="text-sm font-medium"
											>
												หมายเหตุ (ถ้ามี)
											</Label>
											<Textarea
												id="approve-auto-note"
												placeholder="ระบุเหตุผลหรือหมายเหตุเพิ่มเติม..."
												value={approveNote}
												onChange={(e) => setApproveNote(e.target.value)}
												className="min-h-[80px]"
											/>
										</div>
									</AlertDialogHeader>
									<AlertDialogFooter>
										<AlertDialogCancel>ยกเลิก</AlertDialogCancel>
										<AlertDialogAction
											onClick={() => handleApproveWithdraw(false, approveNote)}
											className="bg-blue-600 hover:bg-blue-700"
										>
											<CheckCircle className="mr-2 h-4 w-4" />
											ยืนยันอนุมัติแบบออโต้
										</AlertDialogAction>
									</AlertDialogFooter>
								</AlertDialogContent>
							</AlertDialog>
						)}

						{/* Manual Approval - skips bank API */}
						<AlertDialog
							open={isApproveDialogOpen && approveIsManual === true}
							onOpenChange={(open) => {
								setIsApproveDialogOpen(open)
								if (!open) {
									setApproveNote('')
									setApproveIsManual(undefined)
								}
							}}
						>
							<AlertDialogTrigger
								asChild
								onClick={() => {
									setIsApproveDialogOpen(true)
									setApproveIsManual(true)
								}}
							>
								<Button
									variant="default"
									size="sm"
									className="bg-green-600 hover:bg-green-700"
									disabled={approveWithdraw.isPending}
								>
									{approveWithdraw.isPending ? (
										<Icons.spinner className="mr-2 h-4 w-4 animate-spin" />
									) : (
										<CheckCircle className="mr-2 h-4 w-4" />
									)}
									อนุมัติแบบแมนนวล
								</Button>
							</AlertDialogTrigger>
							<AlertDialogContent className="max-w-md">
								<AlertDialogHeader>
									<div className="flex items-center gap-2">
										<AlertTriangle className="h-5 w-5 text-amber-500" />
										<AlertDialogTitle className="text-lg">
											ยืนยันการอนุมัติการถอนเงินแบบแมนนวล
										</AlertDialogTitle>
									</div>
									<AlertDialogDescription className="space-y-3 text-sm">
										<div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
											<div className="flex items-start gap-2">
												<AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-500" />
												<div className="space-y-1">
													<p className="font-medium text-amber-800">
														กรุณาทำการโอนเงินด้วยตนเองก่อนอนุมัติ
													</p>
													<p className="text-amber-700">
														เมื่อกดยืนยัน ระบบจะทำเครื่องหมายเงินจำนวน{' '}
														<span className="font-semibold">
															฿{amount?.toLocaleString()}
														</span>{' '}
														เป็นสำเร็จโดยไม่ส่งคำสั่งไปยังธนาคาร
													</p>
												</div>
											</div>
										</div>
										<div className="space-y-2">
											<div className="flex justify-between text-sm">
												<span>จำนวนเงิน:</span>
												<span className="font-semibold text-red-600">
													฿{amount?.toLocaleString()}
												</span>
											</div>
											<div className="flex justify-between text-sm">
												<span>บัญชีปลายทาง:</span>
												<span className="font-medium">
													{playerBankProvider} {playerBankAccountNumber}
												</span>
											</div>
											{playerBankAccountName && (
												<div className="flex justify-between text-sm">
													<span>ชื่อบัญชี:</span>
													<span className="font-medium">
														{playerBankAccountName}
													</span>
												</div>
											)}
										</div>
										<p className="text-xs text-gray-600">
											⚠️ กรุณาทำการโอนเงินก่อนกดยืนยันอนุมัติแบบแมนนวล
										</p>
									</AlertDialogDescription>
									<div className="space-y-2">
										<Label
											htmlFor="approve-manual-note"
											className="text-sm font-medium"
										>
											หมายเหตุ (ถ้ามี)
										</Label>
										<Textarea
											id="approve-manual-note"
											placeholder="ระบุเหตุผลหรือหมายเหตุเพิ่มเติม..."
											value={approveNote}
											onChange={(e) => setApproveNote(e.target.value)}
											className="min-h-[80px]"
										/>
									</div>
								</AlertDialogHeader>
								<AlertDialogFooter>
									<AlertDialogCancel>ยกเลิก</AlertDialogCancel>
									<AlertDialogAction
										onClick={() => handleApproveWithdraw(true, approveNote)}
										className="bg-green-600 hover:bg-green-700"
									>
										<CheckCircle className="mr-2 h-4 w-4" />
										ยืนยันอนุมัติแบบแมนนวล
									</AlertDialogAction>
								</AlertDialogFooter>
							</AlertDialogContent>
						</AlertDialog>
					</>
				)}

			{/* Cancel Action - for PENDING/EXPIRED withdrawals */}
			{(status === 'PENDING' || status === 'EXPIRED') &&
				processor === 'MANUAL' && (
					<AlertDialog
						open={isCancelDialogOpen}
						onOpenChange={setIsCancelDialogOpen}
					>
						<AlertDialogTrigger asChild>
							<Button
								variant="destructive"
								size="sm"
								disabled={cancel.isPending}
							>
								{cancel.isPending ? (
									<Icons.spinner className="mr-2 h-4 w-4 animate-spin" />
								) : (
									<X className="mr-2 h-4 w-4" />
								)}
								ยกเลิก
							</Button>
						</AlertDialogTrigger>
						<AlertDialogContent className="max-w-md">
							<AlertDialogHeader>
								<div className="flex items-center gap-2">
									<X className="h-5 w-5 text-red-500" />
									<AlertDialogTitle className="text-lg">
										ยืนยันการยกเลิกการถอนเงิน
									</AlertDialogTitle>
								</div>
								<AlertDialogDescription className="space-y-3 text-sm">
									<div className="rounded-lg border border-red-200 bg-red-50 p-3">
										<div className="flex items-start gap-2">
											<AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-500" />
											<div className="space-y-1">
												<p className="font-medium text-red-800">
													การดำเนินการนี้จะยกเลิกการถอนเงิน
												</p>
												<p className="text-red-700">
													เมื่อกดยืนยัน ระบบจะยกเลิกคำขอถอนเงินจำนวน{' '}
													<span className="font-semibold">
														฿{amount?.toLocaleString()}
													</span>
												</p>
											</div>
										</div>
									</div>
									<div className="space-y-2">
										<div className="flex justify-between text-sm">
											<span>จำนวนเงิน:</span>
											<span className="font-semibold text-red-600">
												฿{amount?.toLocaleString()}
											</span>
										</div>
										<div className="flex justify-between text-sm">
											<span>บัญชีปลายทาง:</span>
											<span className="font-medium">
												{playerBankProvider} {playerBankAccountNumber}
											</span>
										</div>
										{playerBankAccountName && (
											<div className="flex justify-between text-sm">
												<span>ชื่อบัญชี:</span>
												<span className="font-medium">
													{playerBankAccountName}
												</span>
											</div>
										)}
									</div>
									<div className="space-y-2">
										<Label
											htmlFor="cancel-note"
											className="text-sm font-medium"
										>
											หมายเหตุ (ถ้ามี)
										</Label>
										<Textarea
											id="cancel-note"
											placeholder="ระบุเหตุผลในการยกเลิก..."
											value={cancelNote}
											onChange={(e) => setCancelNote(e.target.value)}
											className="min-h-[80px]"
										/>
									</div>
									<p className="text-xs text-gray-600">
										⚠️ การยกเลิกนี้ไม่สามารถย้อนกลับได้
									</p>
								</AlertDialogDescription>
							</AlertDialogHeader>
							<AlertDialogFooter>
								<AlertDialogCancel>ยกเลิก</AlertDialogCancel>
								<AlertDialogAction
									onClick={handleCancelWithdraw}
									className="bg-red-600 hover:bg-red-700"
								>
									<X className="mr-2 h-4 w-4" />
									ยืนยันยกเลิก
								</AlertDialogAction>
							</AlertDialogFooter>
						</AlertDialogContent>
					</AlertDialog>
				)}

			{/* Refund & Retry Actions - for FAILED/EXPIRED withdrawals (only if not already refunded) */}
			{(status === 'FAILED' || status === 'EXPIRED') &&
				!timestamp?.['REFUND'] && (
					<>
						<AlertDialog
							open={isRetryDialogOpen}
							onOpenChange={setIsRetryDialogOpen}
						>
							<AlertDialogTrigger asChild>
								<Button
									variant="outline"
									size="sm"
									className="border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100"
									disabled={retryWithdraw.isPending}
								>
									{retryWithdraw.isPending ? (
										<Icons.spinner className="mr-2 h-4 w-4 animate-spin" />
									) : (
										<RefreshCw className="mr-2 h-4 w-4" />
									)}
									ลองอีกครั้ง
								</Button>
							</AlertDialogTrigger>
							<AlertDialogContent className="max-w-md">
								<AlertDialogHeader>
									<div className="flex items-center gap-2">
										<RefreshCw className="h-5 w-5 text-blue-500" />
										<AlertDialogTitle className="text-lg">
											ยืนยันลองอีกครั้ง
										</AlertDialogTitle>
									</div>
									<AlertDialogDescription className="space-y-3 text-sm">
										<div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
											<div className="flex items-start gap-2">
												<AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-blue-500" />
												<div className="space-y-1">
													<p className="font-medium text-blue-800">
														การดำเนินการนี้จะส่งคำสั่งโอนเงินไปยังธนาคารอีกครั้ง
													</p>
													<p className="text-blue-700">
														เมื่อกดยืนยัน ระบบจะดำเนินการโอนเงินจำนวน{' '}
														<span className="font-semibold">
															฿{amount?.toLocaleString()}
														</span>{' '}
														ไปยังบัญชี {playerBankProvider}{' '}
														{playerBankAccountNumber} ผ่านระบบธนาคารอีกครั้ง
													</p>
												</div>
											</div>
										</div>
										<div className="space-y-2">
											<div className="flex justify-between text-sm">
												<span>จำนวนเงิน:</span>
												<span className="font-semibold text-red-600">
													฿{amount?.toLocaleString()}
												</span>
											</div>
											<div className="flex justify-between text-sm">
												<span>บัญชีปลายทาง:</span>
												<span className="font-medium">
													{playerBankProvider} {playerBankAccountNumber}
												</span>
											</div>
											{playerBankAccountName && (
												<div className="flex justify-between text-sm">
													<span>ชื่อบัญชี:</span>
													<span className="font-medium">
														{playerBankAccountName}
													</span>
												</div>
											)}
										</div>
									</AlertDialogDescription>
								</AlertDialogHeader>
								<AlertDialogFooter>
									<AlertDialogCancel>ยกเลิก</AlertDialogCancel>
									<AlertDialogAction
										onClick={handleRetryWithdraw}
										className="bg-blue-600 hover:bg-blue-700"
									>
										<RefreshCw className="mr-2 h-4 w-4" />
										ยืนยันลองอีกครั้ง
									</AlertDialogAction>
								</AlertDialogFooter>
							</AlertDialogContent>
						</AlertDialog>
						<AlertDialog
							open={isRefundDialogOpen}
							onOpenChange={setIsRefundDialogOpen}
						>
							<AlertDialogTrigger asChild>
								<Button
									variant="outline"
									size="sm"
									className="border-orange-300 bg-orange-50 text-orange-700 hover:bg-orange-100"
									disabled={refund.isPending}
								>
									{refund.isPending ? (
										<Icons.spinner className="mr-2 h-4 w-4 animate-spin" />
									) : (
										<RotateCcw className="mr-2 h-4 w-4" />
									)}
									คืนเงิน
								</Button>
							</AlertDialogTrigger>
							<AlertDialogContent className="max-w-md">
								<AlertDialogHeader>
									<div className="flex items-center gap-2">
										<RotateCcw className="h-5 w-5 text-orange-500" />
										<AlertDialogTitle className="text-lg">
											ยืนยันการคืนเงินถอน
										</AlertDialogTitle>
									</div>
									<AlertDialogDescription className="space-y-3 text-sm">
										<div className="rounded-lg border border-orange-200 bg-orange-50 p-3">
											<div className="flex items-start gap-2">
												<AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-orange-500" />
												<div className="space-y-1">
													<p className="font-medium text-orange-800">
														การดำเนินการนี้จะคืนเงินกลับไปยังกระเป๋าผู้เล่น
													</p>
													<p className="text-orange-700">
														เมื่อกดยืนยัน ระบบจะคืนเงินจำนวน{' '}
														<span className="font-semibold">
															฿{amount?.toLocaleString()}
														</span>{' '}
														กลับไปยังกระเป๋าของผู้เล่น
													</p>
												</div>
											</div>
										</div>
										<div className="space-y-2">
											<div className="flex justify-between text-sm">
												<span>จำนวนเงินที่จะคืน:</span>
												<span className="font-semibold text-green-600">
													฿{amount?.toLocaleString()}
												</span>
											</div>
											<div className="flex justify-between text-sm">
												<span>บัญชีปลายทางเดิม:</span>
												<span className="font-medium">
													{playerBankProvider} {playerBankAccountNumber}
												</span>
											</div>
											{playerBankAccountName && (
												<div className="flex justify-between text-sm">
													<span>ชื่อบัญชี:</span>
													<span className="font-medium">
														{playerBankAccountName}
													</span>
												</div>
											)}
										</div>
										<div className="space-y-2">
											<Label
												htmlFor="refund-note"
												className="text-sm font-medium"
											>
												หมายเหตุ (ถ้ามี)
											</Label>
											<Textarea
												id="refund-note"
												placeholder="ระบุเหตุผลในการคืนเงิน..."
												value={refundNote}
												onChange={(e) => setRefundNote(e.target.value)}
												className="min-h-[80px]"
											/>
										</div>
										<p className="text-xs text-gray-600">
											⚠️ การคืนเงินนี้ไม่สามารถย้อนกลับได้
										</p>
									</AlertDialogDescription>
								</AlertDialogHeader>
								<AlertDialogFooter>
									<AlertDialogCancel>ยกเลิก</AlertDialogCancel>
									<AlertDialogAction
										onClick={handleRefundWithdraw}
										className="bg-orange-600 hover:bg-orange-700"
									>
										<RotateCcw className="mr-2 h-4 w-4" />
										ยืนยันคืนเงิน
									</AlertDialogAction>
								</AlertDialogFooter>
							</AlertDialogContent>
						</AlertDialog>
					</>
				)}
		</div>
	)
}
